import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  Link as MaterialLink,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import useGetPipelinesByEmail from '../../../shared/hooks/useGetPipelinesByEmail';
import ErrorTypography from '../../../shared/ErrorTypography/ErrorTypography';
import { createIconFromName } from '../../../util/iconUtil';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Trans } from 'react-i18next';
import { useDeletePipelineMutation, useDeletePipelinesMutation } from '../../../store/data/dataSlice';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const PipelineTable = (): JSX.Element => {
  const { data, error, isLoading } = useGetPipelinesByEmail();

  const [selectedPid, setSelectedPid] = useState(null as string | null);
  const [selectedName, setSelectedName] = useState('');
  const [open, setOpen] = useState(false);
  const [deletePipelines] = useDeletePipelinesMutation();
  const [deleteSelectMode, setDeleteSelectMode] = useState(false as boolean);
  const [deleteList, setDeleteList] = useState([] as string[])

  const handleDelete = () => {
    setOpen(true);
  };

  const handleClose = (success: boolean) => {
    setOpen(false);
    if(deleteList.length > 0 && success) {
      deletePipelines(deleteList);
      setDeleteList([]);
      setDeleteSelectMode(false);
    }
  };

  const handleCheckboxChange = (pid: string) => {
    if(deleteList.includes(pid)) {
      const newDeleteList = [...deleteList];
      setDeleteList(newDeleteList);
    }
    else {
      setDeleteList([...deleteList, pid]);
    }
  }

  if (error) {
    return <ErrorTypography message={'Error getting pipelines'} />;
  } else {
    return (
      <>
        <TableContainer>
          <Table sx={{ maxWidth: 1000 }} aria-label={'pipelines table'}>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>
                  <Trans>Name</Trans>
                </TableCell>
                <TableCell align="right">PID</TableCell>
                <TableCell width={'120'} align={'center'}>
                {deleteSelectMode ? (
                  <>                  
                    <IconButton color={'success'} onClick={()=>{handleDelete()}}>
                      <CheckCircleRoundedIcon/>
                    </IconButton>
                    <IconButton color={'error'} onClick={()=>{setDeleteSelectMode(false); setDeleteList([])}}>
                      <CancelRoundedIcon/>
                    </IconButton>
                  </>
                ) : (
                  <IconButton color={'error'} onClick={()=>{setDeleteSelectMode(true)}}>
                    <DeleteOutlineRoundedIcon/>
                  </IconButton>
                )}
              </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!isLoading && data ? (
                data.map(row => (
                  <StyledTableRow key={row.pipeline.name} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell component={'th'} scope={'row'}>
                      {createIconFromName(row.pipeline.icon)}
                    </TableCell>
                    <TableCell component={'th'} scope={'row'}>
                      <MaterialLink underline={'hover'} component={Link} to={`/pipeline/${row.pid}`}>
                        {row.pipeline.name}
                      </MaterialLink>
                    </TableCell>
                    <TableCell align={'right'} scope={'row'}>
                      {row.pid}
                    </TableCell>
                    <TableCell align={'center'} scope={'row'}>
                    {deleteSelectMode &&
                      <Checkbox size={'medium'} onChange={()=>{handleCheckboxChange(row.pid)}}/>
                    }
                  </TableCell>
                  </StyledTableRow>
                ))
              ) : (
                <Box sx={{ p: 2 }}>
                  <CircularProgress />
                </Box>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <ConfirmDialog open={open} name={selectedName} handleClose={handleClose} />
      </>
    );
  }
};

export default PipelineTable;
